// Operator /operator/agents view. Mirrors the Claude Code CLI's Agent
// View (2026-05-12) but in the browser, persisted across sessions, with
// cost + duration metrics from agent_runs.

import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

interface Run {
  id: string;
  dispatched_by: string | null;
  subagent_type: string;
  description: string;
  target_path: string | null;
  status: string;
  result_summary: string | null;
  result_severity: string | null;
  dispatched_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  cost_cents: number | null;
}

function timeSince(ts: string): string {
  const now = Date.now();
  const t = new Date(ts.replace(' ', 'T') + (ts.endsWith('Z') ? '' : 'Z')).getTime();
  if (Number.isNaN(t)) return ts;
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function fmtCost(cents: number | null): string {
  if (cents == null) return '—';
  if (cents < 100) return `${cents}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

function statusColor(s: string): string {
  switch (s) {
    case 'running': return T.warning;
    case 'needs_input': return T.warning;
    case 'done': return T.live;
    case 'failed': return T.error;
    case 'queued': return T.inkSoft;
    default: return T.inkMute;
  }
}

function severityColor(s: string | null): string {
  switch (s) {
    case 'critical': return T.error;
    case 'high': return T.error;
    case 'medium': return T.warning;
    case 'low': return T.inkSoft;
    case 'pass': return T.live;
    default: return T.inkMute;
  }
}

export default function Agents() {
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState('');

  const load = () => {
    adminApi
      .agentRuns(filter || undefined)
      .then((d) => {
        setRuns(d.runs as Run[]);
        setCounts(d.counts);
      })
      .catch(() => setRuns([]));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const totalCost = useMemo(() => {
    if (!runs) return 0;
    return runs.reduce((s, r) => s + (r.cost_cents ?? 0), 0);
  }, [runs]);

  return (
    <>
      <PageHeader
        title="Agents"
        subtitle="Every subagent dispatch (deploy-gate, parallel work, cron audits) lands here. Refreshes every 15s."
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <Card label="Running" value={counts.running ?? 0} accent={(counts.running ?? 0) > 0} color={T.warning} />
        <Card label="Needs input" value={counts.needs_input ?? 0} accent={(counts.needs_input ?? 0) > 0} color={T.warning} />
        <Card label="Done (7d)" value={counts.done ?? 0} />
        <Card label="Failed (7d)" value={counts.failed ?? 0} color={(counts.failed ?? 0) > 0 ? T.error : undefined} />
        <Card label="Cost (visible)" value={fmtCost(totalCost)} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['', 'running', 'needs_input', 'done', 'failed'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setFilter(s)}
            style={{
              padding: '6px 14px',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              background: filter === s ? T.accent : 'transparent',
              color: filter === s ? T.bg : T.inkSoft,
              border: `1px solid ${filter === s ? T.accent : T.rule}`,
              borderRadius: 4,
            }}
          >
            {s || 'all'}
          </button>
        ))}
      </div>

      {runs === null ? (
        <div style={{ padding: 32, color: T.inkMute, fontStyle: 'normal' }}>Loading…</div>
      ) : runs.length === 0 ? (
        <div
          style={{
            padding: 48,
            background: T.bgCard,
            border: `1px dashed ${T.rule}`,
            borderRadius: 8,
            textAlign: 'center',
            color: T.inkMute,
            fontStyle: 'normal',
          }}
        >
          No agent runs yet. Dispatched agents will appear here as soon as they record their first state update.
        </div>
      ) : (
        <div style={{ background: T.bgCard, border: `1px solid ${T.rule}`, borderRadius: 8, overflow: 'hidden' }}>
          {runs.map((r) => (
            <div
              key={r.id}
              style={{
                padding: '14px 16px',
                borderTop: `1px solid ${T.rule}`,
                display: 'grid',
                gridTemplateColumns: '120px 1fr 100px 80px 80px',
                gap: 12,
                alignItems: 'baseline',
                fontSize: 13,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: statusColor(r.status),
                    fontWeight: 500,
                  }}
                >
                  {r.status}
                </div>
                {r.result_severity && (
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: severityColor(r.result_severity),
                      marginTop: 2,
                    }}
                  >
                    {r.result_severity}
                  </div>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: T.ink, fontWeight: 500 }}>{r.description}</div>
                <div
                  style={{
                    fontFamily: T.font.mono,
                    fontSize: 11,
                    color: T.inkSoft,
                    marginTop: 2,
                  }}
                >
                  {r.subagent_type}
                  {r.target_path && ` · ${r.target_path}`}
                  {r.dispatched_by && ` · by ${r.dispatched_by}`}
                </div>
                {r.result_summary && (
                  <div
                    style={{
                      fontSize: 12,
                      color: T.inkSoft,
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {r.result_summary.slice(0, 240)}
                    {r.result_summary.length > 240 && '…'}
                  </div>
                )}
              </div>
              <div style={{ color: T.inkSoft, fontFamily: T.font.mono, fontSize: 12, textAlign: 'right' }}>
                {timeSince(r.dispatched_at)}
              </div>
              <div style={{ color: T.inkSoft, fontFamily: T.font.mono, fontSize: 12, textAlign: 'right' }}>
                {fmtDuration(r.duration_ms)}
              </div>
              <div style={{ color: T.inkSoft, fontFamily: T.font.mono, fontSize: 12, textAlign: 'right' }}>
                {fmtCost(r.cost_cents)}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Card({
  label,
  value,
  accent,
  color,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
  color?: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {accent && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: color ?? T.accent,
          }}
        />
      )}
      <div style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: T.font.display,
          fontSize: 28,
          fontWeight: 400,
          color: color && accent ? color : T.ink,
        }}
      >
        {value}
      </div>
    </div>
  );
}
